import { Observable } from 'rxjs';
import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Page } from '../../models/page';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { ControlContainer, FormGroupDirective, FormControl, AbstractControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'app-paginated-autocomplete-manual',
  templateUrl: './paginated-autocomplete-manual.component.html',
  styleUrls: ['./paginated-autocomplete-manual.component.css'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective
    }
  ]
})
export class PaginatedAutocompleteManualComponent implements OnInit, OnDestroy, OnChanges {
  resultList: any[] = [];
  gettingData: boolean = false;
  page: Page = new Page;

  @Input() required: boolean = false;
  @Input() inputType: string = "text";
  @Input() dataSrc!: (pageMeta: Page) => Observable<any>;
  @Input() displayWithFunc = (item?: any) => {
    return item ? item : undefined;
  }
  @Input() controlName!: string;
  @Input() readonly!: boolean;
  @Input() label!: string;
  @Input() control!: FormControl;
  @Input() placeholder: string = '';
  @Input() optional: boolean = false;
  @Input() pageSize: number = 10;
  @Input() onlyValidData?: boolean = false
  autocompleteSelectionVal: any;
  @Output() autoCompleteOptionSelected: EventEmitter<any> = new EventEmitter();
  @Output() textChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() showAddButton: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('paginatedAutocomplete') autoCompleteInstance!: MatAutocomplete;
  @Output() isAutocomplete: EventEmitter<boolean> = new EventEmitter();

  memoizedDataSrc: any;
  memoizedResults:any = {};
  constructor() {

  }

  private buildMemoizedDataSrc() {
    if (!this.dataSrc) {
      this.memoizedDataSrc = undefined;
      return;
    }
    const memoize = (func:any) => {
      return async (...args:any) => {
        const argsKey = JSON.stringify(args);
        if (!this.memoizedResults[argsKey]) {
          await new Promise((resolve) => {
            func(...args).subscribe((res: any) => {
              this.memoizedResults[argsKey] = res;
              resolve(res);
            });
          });
        }
        return this.memoizedResults[argsKey];
      };
    };
    this.memoizedDataSrc = memoize(this.dataSrc);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSrc']) {
      // Rebuild memoized wrapper whenever data source function changes
      this.resetAndClearStoredResults();
      this.buildMemoizedDataSrc();
    }
  }

  ngOnInit(): void {
    if (!this.control) {
      console.log('Form control is not defined');
      // Other initialization logic
      return
    } 

    if(this.control.value?.currencyCode){
      console.log('setting defualt value==>',this.control.value?.currencyCode)
      this.autocompleteSelectionVal = this.control.value?.currencyCode
      this.autoCompleteOptionSelected.emit(this.control.value);
    }

    this.page.size = this.pageSize;
    // Initialize memoized source if available at init time
    this.buildMemoizedDataSrc();

    this.control.valueChanges.pipe(
      debounceTime(300),
      tap(() => {
        this.resetAndClearStoredResults();
      }),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      if (!this.control) {
        return;
      }
      this.page.pageNumber = 0;
      this.page.totalElements = 0;
      this.page.totalPages = 0;

      if (this.control.pristine) {
        // The control was marked pristine, it must have been reset, we need to remove stored results
        this.resetAndClearStoredResults();
      }
      if (this.onlyValidData && value && typeof value !== 'object') {
        this.autocompleteSelectionVal = undefined;
        this.control.setErrors({ "autocompleteOptionNotSelected": true });
        // this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }

      if (!value || typeof value == "undefined" || value == "") {
        this.page.serachParam = "";
        this.autoCompleteOptionSelected.next(null);
        this.clickCallback(false, false);
        this.textChanged.emit("")
      } else {
        if (typeof value == "string") {
          this.textChanged.emit(value)
          this.page.serachParam = value;
          this.clickCallback(false, false);
          this.textChanged.emit(value)
        }
      }
    });
  }

  /** This can be improved a lot with memoization, need to implement in future */
  clickCallback(reset: boolean = false, concat: boolean = true) {
    if (!this.control) {
      console.error('FormControl is not defined.');
      return;
    }

    if (reset) {
      this.resultList = [];
      this.page.pageNumber = 0;
      this.page.totalElements = 0;
      this.page.totalPages = 0;
      this.page.serachParam = this.control.value ? this.displayWithFunc(this.control.value) : "";
    }

    // Call already in progress
    if (this.gettingData) {
      //console.log("A previous call is already in progress!");
    } else {
      this.gettingData = true;
      const handleSuccess = (data: any) => {
        if (data && data.rows && data.rows.length > 0) {
          this.showAddButton.emit(false);
        } else {
          this.showAddButton.emit(true);
        }
        this.resultList = concat ? this.resultList.concat(data.rows) : data.rows;
        this.page.totalElements = data.page?.totalElements;
        // Returns pages as if they start from 0
        this.page.totalPages = data.page?.totalPages ? data.page.totalPages + 1 : undefined;
        this.gettingData = false;
      };
      const handleError = (_error: any) => {
        this.gettingData = false;
      };

      if (this.memoizedDataSrc) {
        this.memoizedDataSrc(this.page)?.then(handleSuccess, handleError);
      } else if (this.dataSrc) {
        // Fallback: no memoized wrapper yet, call the source directly
        this.dataSrc(this.page).subscribe(handleSuccess, handleError);
      } else {
        // No data source available
        this.gettingData = false;
      }
    }
  }

  autoCompleteEvent(type: "opened" | "closed" = "opened") {
    // Need to give sometime for panel to load into the DOM
    setTimeout(() => {
      const panel = this.autoCompleteInstance.panel?.nativeElement;
      const scrollfunction = () => {
        if (panel.scrollHeight - panel.scrollTop === panel.clientHeight) {
          this.page.pageNumber++;
          this.clickCallback();
        };
      }

      if (type == "opened" && panel) {
        panel.addEventListener('scroll', scrollfunction);
      } else if (type == "closed" && panel) {
        panel.removeEventListener('scroll', scrollfunction);
        this.resultList = [];
      }
    }, 200);
  }

  selectionMadeEvent(option: any) {
    if (!this.control) {
      console.error('FormControl is not defined.');
      return;
    }
    this.autocompleteSelectionVal = option;
    this.control.setErrors({
      "autocompleteOptionNotSelected": false
    });
    this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.autoCompleteOptionSelected.emit(option);
  }

  ngOnDestroy(): void {
  }

  resetAndClearStoredResults() {
    this.memoizedResults = {};
    this.resultList = [];
  }
}
